import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaBoxOpen,
  FaTag,
  FaImage,
  FaAlignLeft,
  FaStar,
} from "react-icons/fa";
import BASE_URL, { getImageUrl } from "../../BASEURL";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || BASE_URL;

  const [categories, setCategories] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);

  // LOAD PRODUCT
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/product/${id}`);
        const data = await res.json();
        const p = data?.product || data?.data || data;

        const imagesArr = Array.isArray(p?.images)
          ? p.images
          : p?.image
          ? [p.image]
          : [];

        // Parse sizes to array of objects
        let initialSizes = [
          { size: "", price: "", discountPrice: "" }
        ];

        if (Array.isArray(p?.sizes) && p.sizes.length > 0) {
          // Check if it's new schema [{size, price, discountPrice}]
          if (typeof p.sizes[0] === 'object') {
            initialSizes = p.sizes.map(s => ({
              size: s.size || "",
              price: s.price || "",
              discountPrice: s.discountPrice || ""
            }));
          } else {
            // Old schema flat array of strings fallback
            initialSizes = p.sizes.map(s => ({
              size: s,
              price: p.price || "",
              discountPrice: p.discountPrice || ""
            }));
          }
        }

        setForm({
          productName: p?.productName || p?.name || "",
          rating: p?.rating || "",
          categoryId: (typeof p?.categoryId === "string" ? p.categoryId : p?.categoryId?._id) || "",
          sizes: initialSizes,
          images: imagesArr,
          description: p?.description || "",
          isActive: p?.isActive ?? true,
          isBestSeller: !!p?.isBestSeller,
          isSale: !!p?.isSale,
        });
      } catch (err) {
        toast.error("Failed to load product");
      }
    };

    fetchProduct();
  }, [id, backendUrl]);

  // LOAD CATEGORIES
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/category`);
        const data = await res.json();
        setCategories(data?.data || data || []);
      } catch (err) {
        console.log(err);
      }
    };
    loadCategories();
  }, [backendUrl]);

  // HANDLE CHANGE
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...form.sizes];
    updatedSizes[index][field] = value;
    setForm((prev) => ({
      ...prev,
      sizes: updatedSizes,
    }));
  };

  const addSize = () => {
    setForm((prev) => ({
      ...prev,
      sizes: [
        ...prev.sizes,
        { size: "", price: "", discountPrice: "" },
      ],
    }));
  };

  const removeSize = (index) => {
    if (form.sizes.length === 1) return;
    const updatedSizes = form.sizes.filter((_, i) => i !== index);
    setForm((prev) => ({
      ...prev,
      sizes: updatedSizes,
    }));
  };

  // UPDATE PRODUCT
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const existingImages = Array.isArray(form.images)
        ? form.images.filter(img => typeof img === 'string' && img.trim())
        : [];

      const formData = new FormData();
      
      formData.append("productName", form.productName);
      formData.append("rating", Number(form.rating));
      formData.append("categoryId", form.categoryId);
      formData.append("description", form.description);
      formData.append("isActive", form.isActive);
      formData.append("isBestSeller", form.isBestSeller);
      formData.append("isSale", form.isSale);

      // Send sizes array of objects instead of array of strings
      formData.append(
        "sizes",
        JSON.stringify(
          form.sizes.map((item) => ({
            size: item.size,
            price: Number(item.price),
            discountPrice: Number(item.discountPrice),
          }))
        )
      );

      existingImages.forEach((img, index) => {
        formData.append(`existingImages[${index}]`, img);
      });

      imageFiles.forEach((file) => {
        formData.append(`images`, file);
      });

      const token = localStorage.getItem("adminToken");

      const res = await fetch(`${backendUrl}/api/product/${id}`, {
        method: "PUT",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Update failed");
      }

      toast.success("Product updated successfully");
      setTimeout(() => {
        navigate("/admin/products");
      }, 1500);

    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!form) {
    return (
      <div className="h-screen flex justify-center items-center font-bold text-xl">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-5">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* HEADER */}
        <div className="bg-black px-8 py-6">
          <h2 className="text-3xl font-bold text-white">Edit Product</h2>
          <p className="text-gray-300 mt-1 text-sm">
            Update your product details
          </p>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-8 space-y-7">
          {/* PRODUCT NAME */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaBoxOpen /> Product Name
            </label>
            <input
              type="text"
              name="productName"
              value={form.productName}
              onChange={handleChange}
              placeholder="Enter product name"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* CATEGORY */}
            <div>
              <label className="font-semibold mb-2 block">Category</label>
              <select
                name="categoryId"
                value={form.categoryId}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-4 py-3"
                required
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* RATING */}
            <div>
              <label className="font-semibold mb-2 flex items-center gap-2 block">
                <FaStar /> Rating
              </label>
              <input
                type="number"
                step="0.1"
                name="rating"
                value={form.rating}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-4 py-3"
              />
            </div>
          </div>

          {/* SIZES */}
          <div className="border border-gray-200 p-5 rounded-2xl bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <label className="font-semibold flex items-center gap-2 m-0">
                <FaTag /> Sizes & Prices
              </label>
              <button
                type="button"
                onClick={addSize}
                className="bg-black text-white px-4 py-2 rounded-xl text-sm hover:opacity-90 transition"
              >
                + Add Size
              </button>
            </div>

            {form.sizes.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                <input
                  type="text"
                  placeholder="Size (L, XL, 500g)"
                  value={item.size}
                  onChange={(e) => handleSizeChange(index, "size", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={item.price}
                  onChange={(e) => handleSizeChange(index, "price", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <input
                  type="number"
                  placeholder="Discount Price"
                  value={item.discountPrice}
                  onChange={(e) => handleSizeChange(index, "discountPrice", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => removeSize(index)}
                  className="bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-xl px-4 py-3 w-full transition"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* IMAGES */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaImage /> Product Images
            </label>

            {/* IMAGE PREVIEW */}
            {form.images && Array.isArray(form.images) && form.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {form.images.map((img, index) => (
                  <div key={index} className="relative border rounded-2xl overflow-hidden bg-gray-100 h-32">
                    <img
                      src={getImageUrl(img)}
                      alt={`product-${index}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/300x300?text=No+Image";
                      }}
                    />
                    {index === 0 && (
                      <span className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded">
                        Main
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
              className="mt-4 block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-black file:text-white
                hover:file:bg-gray-800"
            />
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {imageFiles.map((file, index) => (
                  <div key={index} className="border rounded-2xl overflow-hidden h-32 bg-gray-100">
                    <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaAlignLeft /> Description
            </label>
            <ReactQuill
              theme="snow"
              value={form.description}
              onChange={(value) => setForm((prev) => ({ ...prev, description: value }))}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              className="bg-white rounded-xl"
            />
          </div>

          {/* CHECKBOXES */}
          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} className="w-4 h-4" />
              <span className="font-medium">Active</span>
            </label>
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input type="checkbox" name="isBestSeller" checked={form.isBestSeller} onChange={handleChange} className="w-4 h-4" />
              <span className="font-medium">Best Seller</span>
            </label>
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input type="checkbox" name="isSale" checked={form.isSale} onChange={handleChange} className="w-4 h-4" />
              <span className="font-medium">On Sale</span>
            </label>
          </div>

          {/* BUTTONS */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate("/admin/products")}
              className="px-6 py-3 rounded-xl border border-gray-300 hover:bg-gray-100 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 rounded-xl bg-black text-white hover:bg-gray-800 disabled:opacity-50 transition font-medium"
            >
              {loading ? "Updating..." : "Update Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
